// ══════════════════════════════════════════════════════════
// NOW PLAYING SERVICE — Backend PHP Contract
//
// Consulta estado de estación, metadata de pista actual,
// y radio al aire desde el backend PHP.
//
// NO consulta CentovaCast directamente.
// NO expone credenciales, rutas internas ni URLs administrativas.
// Todo el texto mostrado al usuario está sanitizado.
//
// Endpoints:
//   GET /api/v1/stations/{stationId}/status
//   GET /api/v1/stations/{stationId}/now-playing
//   GET /api/v1/radios/{radioId}/on-air
// ══════════════════════════════════════════════════════════

import { backendApiRequest, RfwBackendError } from '@/services/rfwBackend';
import type {
  StationStatusResult,
  NowPlayingResult,
  RadioOnAirResult,
} from '@/types/nowPlaying';

// ── ERROR MAPPER ──

/**
 * Traduce errores HTTP a mensajes amigables.
 * Nunca expone respuestas crudas del proveedor (CentovaCast, etc).
 * Nunca muestra rutas internas ni credenciales de mount.
 */
function mapNowPlayingError(err: unknown): never {
  if (err instanceof RfwBackendError) {
    switch (err.statusCode) {
      case 401:
        throw new Error('Sesión expirada. Iniciá sesión para ver el estado de la estación.');
      case 403:
        throw new Error('No tenés permisos para ver esta estación.');
      case 404:
        throw new Error('Estación no encontrada.');
      case 409:
        throw new Error('La estación no está configurada correctamente.');
      case 429:
        throw new Error('Demasiadas consultas. Esperá unos segundos.');
      default:
        if (err.statusCode >= 500) {
          throw new Error('El proveedor de streaming no está disponible en este momento.');
        }
        throw new Error(err.message || 'Error al consultar el estado de la estación.');
    }
  }
  throw err instanceof Error ? err : new Error('Error desconocido al consultar now-playing.');
}

// ── SANITIZER ──

/**
 * Sanitiza texto proveniente del backend antes de mostrarlo.
 * Escapa caracteres < > para prevenir renderizado accidental de HTML.
 * Trunca strings excesivamente largos.
 */
function sanitizeText(text: string | null | undefined): string | null {
  if (text == null) return null;
  const cleaned = String(text)
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .trim();
  // Limitar a 500 caracteres como protección
  return cleaned.slice(0, 500) || null;
}

function sanitizeResult<T extends Record<string, unknown>>(data: T): T {
  const clone = { ...data };
  const textFields = ['title', 'artist', 'album'] as const;
  for (const field of textFields) {
    if (field in clone && typeof clone[field] === 'string') {
      clone[field] = sanitizeText(clone[field] as string) as T[typeof field];
    }
  }
  return clone;
}

// ── API METHODS ──

/**
 * Consulta el estado operacional de una estación.
 * GET /api/v1/stations/{stationId}/status
 *
 * Devuelve: online/offline, oyentes, estado del proveedor, stream_url pública.
 * NUNCA devuelve credenciales, puertos administrativos ni rutas internas.
 */
export async function getStationStatus(
  stationId: string,
): Promise<StationStatusResult> {
  try {
    const response = await backendApiRequest<StationStatusResult>(
      `/api/v1/stations/${encodeURIComponent(stationId)}/status`,
      { method: 'GET' },
    );
    return sanitizeResult(response.data) as StationStatusResult;
  } catch (err: unknown) {
    mapNowPlayingError(err);
    throw err;
  }
}

/**
 * Consulta la metadata de la pista actual de una estación.
 * GET /api/v1/stations/{stationId}/now-playing
 *
 * Devuelve: título, artista, álbum, artwork_url, duración, progreso,
 * oyentes y estado del proveedor.
 *
 * Campos opcionales (title, artist, album, artwork_url, etc.) pueden
 * ser null si el proveedor no los entrega. La UI debe manejarlo.
 */
export async function getNowPlaying(
  stationId: string,
): Promise<NowPlayingResult> {
  try {
    const response = await backendApiRequest<NowPlayingResult>(
      `/api/v1/stations/${encodeURIComponent(stationId)}/now-playing`,
      { method: 'GET' },
    );
    const sanitized = sanitizeResult(response.data as unknown as Record<string, unknown>);
    return sanitized as NowPlayingResult;
  } catch (err: unknown) {
    mapNowPlayingError(err);
    throw err;
  }
}

/**
 * Consulta qué estación está al aire para una radio.
 * GET /api/v1/radios/{radioId}/on-air
 *
 * Devuelve la estación activa y su metadata actual.
 * Útil para páginas públicas de radio que necesitan mostrar
 * el estado de transmisión sin conocer el station_id interno.
 */
export async function getRadioOnAir(
  radioId: string,
): Promise<RadioOnAirResult> {
  try {
    const response = await backendApiRequest<RadioOnAirResult>(
      `/api/v1/radios/${encodeURIComponent(radioId)}/on-air`,
      { method: 'GET' },
    );
    const sanitized = sanitizeResult(response.data as unknown as Record<string, unknown>);
    return sanitized as RadioOnAirResult;
  } catch (err: unknown) {
    mapNowPlayingError(err);
    throw err;
  }
}

// ── EXPORT AGRUPADO ──

export const nowPlayingService = {
  getStationStatus,
  getNowPlaying,
  getRadioOnAir,
};